import React, { useEffect, useState } from 'react';
import Input from 'Shared/Fields/Text';
import { Checkbox } from 'Shared/Fields/Toggle';
import Regex from 'Shared/Models/Regex.type';
import { H2, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import * as style from 'Shared/Style';
import FeedbackButton, { ButtonTheme } from 'Shared/Button/Feedback';
import { styled, StyledProps } from '@glitz/react';
import AddressType from '../../../Features/Checkout/Pages/Checkout/Address.type';
import AddressLabels from '../../../Features/Checkout/Pages/Checkout/Addresses+AddressLabels.type';

const FormGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridGap: '16px',
  ['@media ' + style.mediaMinLarge]: {
    gridTemplateColumns: '1fr 1fr',
  },
  marginBottom: '20px',
});

const TwoColumnInput = styled(Input, {
  ['@media ' + style.mediaMinLarge]: {
    gridColumn: 'span 2',
  },
});

const FormContentHeading = styled(H2, {
  marginBottom: '16px',
});

const FormContentDescription = styled(Paragraph, {
  marginBottom: '16px',
  color: style.colors.monochrome.darkGrey,
});

const StyledErrorMessage = styled.div({
  color: style.colors.negativeColors.negative,
  userSelect: 'none',
  fontSize: '16px',
  padding: {
    y: '12px'
  }
});

type FormStateType = {
  shippingAddress: AddressType;
  billingAddress?: AddressType;
  useSeparateShippingAddress: boolean;
  showError: boolean;
};

type CustomerDetailsProps = StyledProps & {
  shippingAddress: AddressType;
  billingAddress: AddressType;
  useSeparateShippingAddress: boolean;
  continueToDeliveryLabel: string;
  onPreUpdate: () => void;
  onUpdate: (shippingAddress: AddressType, billingAddress: AddressType, useSeparateShippingAddress: boolean) => Promise<any>;
  addressLabels?: AddressLabels;
  regex?: Regex;
  errorMessage: string;
};

const CustomerDetails = ({
  shippingAddress,
  billingAddress,
  useSeparateShippingAddress,
  continueToDeliveryLabel,
  onPreUpdate,
  onUpdate,
  addressLabels,
  regex,
  errorMessage
}: CustomerDetailsProps) => {
  const [formState, setFormState] = useState<FormStateType>({
    shippingAddress: { ...shippingAddress },
    billingAddress: { ...billingAddress },
    useSeparateShippingAddress: useSeparateShippingAddress,
    showError: false,
  });

  function updateAddress() {
    if (inputIsValid()) {
      if (!formState.useSeparateShippingAddress) {
        updateFormState('shippingAddress', {});
      }
      return onUpdate(formState.shippingAddress, formState.billingAddress, formState.useSeparateShippingAddress);
    }

    return Promise.reject(null);
  }

  function inputIsValid() {
    const address1 = addressIsValid(formState.billingAddress, true);

    if (address1 && formState.useSeparateShippingAddress) {
      return addressIsValid(formState.shippingAddress, false);
    }

    return address1;
  }

  function addressIsValid(address: AddressType, isMainAddress: boolean) {
    if (!address) {
      return false;
    }

    if (!address.firstName || !expIsValid(address.firstName, regex.name)) {
      return false;
    }

    if (!address.lastName || !expIsValid(address.lastName, regex.name)) {
      return false;
    }

    if (!address.line1 || !expIsValid(address.line1, regex.address)) {
      return false;
    }

    // Validate if line2 has value, if so test with regex
    if (address.line2 && !expIsValid(address.line2, regex.address)) {
      return false;
    }

    if (!address.city || !expIsValid(address.city, regex.city)) {
      return false;
    }

    if (!address.postalCode || !expIsValid(address.postalCode, regex.postalCode)) {
      return false;
    }
    if (isMainAddress) {
      if (!address.email || !expIsValid(address.email, regex.email)) {
        return false;
      }

      if (!address.cellPhone || !expIsValid(address.cellPhone, regex.phoneNumber)) {
        return false;
      }
    }

    return true;
  }

  function expIsValid(value: string, expression: string) {
    if (!expression || expression === '') {
      return true;
    }
    const regex = expression.includes('\p') ? new RegExp(expression, 'u') : new RegExp(expression);
    return regex.test(value);
  }

  function updateFormState<T>(key: string, value: T) {
    setFormState((prevState) => {
      const keys = key.split('.');
      const newState = { ...prevState };
      let current = newState;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newState;
    });
  }

  return (
    <div>
      <FormContentDescription>The address should be in United Kingdom.</FormContentDescription>
      <CustomerDetailInputs
        address={formState.billingAddress}
        addressLabels={addressLabels}
        showError={formState.showError}
        regex={regex}
        primaryKey="billingAddress"
        onChange={updateFormState}
        isMainAddress={true}
      />

      <Checkbox
        style={{
          marginBottom: '20px',
        }}
        checked={formState.useSeparateShippingAddress}
        onChange={(e) => updateFormState('useSeparateShippingAddress', e.currentTarget.checked)}
      >
        {addressLabels.useSeparateShippingAddress}
      </Checkbox>

      {formState.useSeparateShippingAddress && (
        <>
          <FormContentHeading fontSize={ResponsiveSize.D16_M16}>{addressLabels.shippingHeading}</FormContentHeading>
          <FormContentDescription>The address should be in United Kingdom.</FormContentDescription>
          <CustomerDetailInputs
            address={formState.shippingAddress}
            addressLabels={addressLabels}
            showError={formState.showError}
            regex={regex}
            primaryKey="shippingAddress"
            onChange={updateFormState}
            isMainAddress={false}
          />
        </>
      )}
      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      <div>
        <FeedbackButton
          type='submit'
          onClick={() => updateAddress()}
          theme={ButtonTheme.BlackToWhite}
        >
          {continueToDeliveryLabel}
        </FeedbackButton>
      </div>
    </div >
  );
};

type CustomerDetailInputProps = {
  address: AddressType;
  addressLabels: AddressLabels;
  showError: boolean;
  regex: Regex;
  primaryKey: string;
  isMainAddress: boolean;
  onChange: (key: string, value: string) => void;
};

function CustomerDetailInputs({
  address,
  addressLabels,
  showError,
  regex,
  primaryKey,
  isMainAddress,
  onChange,
}: CustomerDetailInputProps) {
  return (
    <FormGrid>
      <Input
        value={address.firstName}
        required
        type="text"
        name="firstName"
        pattern={regex?.name}
        placeholder={addressLabels?.firstName}
        onChange={(e) => onChange(`${primaryKey}.firstName`, e.currentTarget.value)}
        errorText={addressLabels?.invalidFirstName}
      />
      <Input
        value={address.lastName}
        required
        type="text"
        name="lastName"
        pattern={regex?.name}
        placeholder={addressLabels?.lastName}
        onChange={(e) => onChange(`${primaryKey}.lastName`, e.currentTarget.value)}
        errorText={addressLabels?.invalidLastName}
      />
      {isMainAddress && <TwoColumnInput
        value={address.email}
        required
        type="email"
        name="email"
        placeholder={addressLabels?.email}
        onChange={(e) => onChange(`${primaryKey}.email`, e.currentTarget.value)}
        pattern={regex?.email}
        errorText={addressLabels?.invalidEmail}
      />}
      <TwoColumnInput
        value={address.organization}
        type="text"
        name="organization"
        pattern={regex?.name}
        placeholder={addressLabels?.organization}
        onChange={(e) => onChange(`${primaryKey}.organization`, e.currentTarget.value)}
        errorText={addressLabels?.invalidOrganization}
      />
      <TwoColumnInput
        value={address.line1}
        required
        type="text"
        name="addressLine1"
        maxLength={60}
        pattern={regex?.address}
        placeholder={addressLabels?.line1}
        onChange={(e) => onChange(`${primaryKey}.line1`, e.currentTarget.value)}
        errorText={addressLabels?.invalidLine1}
      />
      <TwoColumnInput
        value={address.line2}
        type="text"
        name="addressLine2"
        maxLength={60}
        pattern={regex?.address}
        placeholder={addressLabels?.line2}
        onChange={(e) => onChange(`${primaryKey}.line2`, e.currentTarget.value)}
        errorText={addressLabels?.invalidLine2}
      />
      <Input
        value={address.city}
        required
        type="text"
        pattern={regex?.city}
        placeholder={addressLabels?.city}
        onChange={(e) => onChange(`${primaryKey}.city`, e.currentTarget.value)}
        errorText={addressLabels?.invalidCity}
      />
      <Input
        value={address.postalCode}
        required
        name="postalCode"
        pattern={regex?.postalCode}
        forceShowErrorMessage={showError}
        placeholder={addressLabels?.postalCode}
        onChange={(e) => onChange(`${primaryKey}.postalCode`, e.currentTarget.value)}
        errorText={addressLabels?.invalidPostalCode}
      />
      {isMainAddress &&
        <TwoColumnInput
          value={address.cellPhone}
          required
          type="text"
          name="cellPhone"
          pattern={regex?.phoneNumber}
          placeholder={addressLabels?.cellPhone}
          onChange={(e) => onChange(`${primaryKey}.cellPhone`, e.currentTarget.value)}
          errorText={addressLabels?.invalidCellPhone}
        />}
    </FormGrid >
  );
}

export default styled(CustomerDetails);
